import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerProjectEngagementsApi } from '../../api/partner-subpartners-engagements';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

/** Projects & Programmes / Partnership History (spec sections 36, 59) - real linked engagements, genuinely distinct from portal access. */
export function PartnerProjectsTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const engagementsQuery = useQuery({ queryKey: ['partner-engagements', partnerId], queryFn: () => partnerProjectEngagementsApi.list(partnerId) });
  const projectsQuery = useQuery({ queryKey: ['all-projects-for-engagement'], queryFn: () => projectsApi.list() });
  const { register, handleSubmit, reset } = useForm<{ projectId: string; roleOnProject: string; engagementValue: string; startDate: string; endDate: string }>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-engagements', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: { projectId: string; roleOnProject: string; engagementValue: string; startDate: string; endDate: string }) =>
      partnerProjectEngagementsApi.create(partnerId, { ...values, projectId: Number(values.projectId), engagementValue: values.engagementValue ? Number(values.engagementValue) : undefined }),
    onSuccess: () => { showSuccess('Project engagement linked.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to link project.'),
  });
  const performanceMutation = useMutation({
    mutationFn: ({ id, rating }: { id: number; rating: string }) => partnerProjectEngagementsApi.recordPerformance(id, { performanceRating: rating }),
    onSuccess: () => { showSuccess('Performance recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        {engagementsQuery.data && (
          <DataTable
            rows={engagementsQuery.data.data}
            rowKey={(e) => e.id}
            emptyMessage="No project engagements on file yet."
            columns={[
              { header: 'Project', render: (e) => `${e.project_name} (${e.project_reference})` },
              { header: 'Role', render: (e) => e.role_on_project ?? '-' },
              { header: 'Value', render: (e) => e.engagement_value ? `${e.engagement_value} ${e.engagement_currency}` : '-' },
              { header: 'Audit Findings', render: (e) => e.audit_findings ?? '-' },
              {
                header: 'Performance', render: (e) => (
                  <select value={e.performance_rating ?? ''} onChange={(ev) => performanceMutation.mutate({ id: e.id, rating: ev.target.value })}>
                    <option value="">- not rated -</option>
                    <option value="excellent">Excellent</option><option value="good">Good</option><option value="satisfactory">Satisfactory</option>
                    <option value="unsatisfactory">Unsatisfactory</option><option value="poor">Poor</option>
                  </select>
                ),
              },
            ]}
          />
        )}

        <h3>Link a Project</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Project
            <select {...register('projectId', { required: true })}>
              <option value="">- choose -</option>
              {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name} ({p.project_reference})</option>)}
            </select>
          </label>
          <label>Role<input {...register('roleOnProject')} /></label>
          <label>Value<input type="number" {...register('engagementValue')} /></label>
          <label>Start Date<input type="date" {...register('startDate')} /></label>
          <label>End Date<input type="date" {...register('endDate')} /></label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Linking…' : 'Link'}</button>
        </form>
      </div>
    </div>
  );
}
